import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
} from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useCallback, useEffect, useState, type RefObject } from 'react';

/**
 * The shape of the context every message template rendered in this placement provides.
 */
export type MessageTemplateContext = {
	message: string;
};

/**
 * Props the product passes into the placement. The backlog owns this data, so it
 * is supplied by Jira rather than carried in the message template context.
 */
export type PlacementRenderProps = {
	// Props used by the `jira-backlog-agent-setup-banner` message template.
	/** Opens the agent setup experience in the product. */
	onSetUpClick?: () => void;
	triggerRef?: RefObject<HTMLElement>;

	/** SSR-preloaded placement response. */
	preloadedInitialData?: DefaultMessagesResponse;
	/** Called when the message is dismissed so the product can hide the placement. */
	onDismiss?: () => void;
};

/** Props each message receives: the product props plus placement-owned dismissal. */
export type MessageRenderProps = PlacementRenderProps & {
	/** Removes this message from the placement for the rest of the session. */
	dismiss: () => void;
};

const placementConfig = {
	placementId: `backlog-placement`,
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
		hideInIsolatedCloud: false, // Set to true if your placement should not be shown in IC
	},
};

const createFrontend = createPlacementFrontend(placementConfig);

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<MessageRenderProps>;
	},
	PlacementRenderProps
> = createFrontend<PlacementRenderProps>((Messages) => (appProps) => {
	const { preloadedInitialData, onDismiss, ...messageProps } = appProps;
	const [response, setResponse] = useState(preloadedInitialData);
	const isDone = response !== undefined;
	const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
		autoRefetch: { isEnabled: !isDone, updateKey: 'backlog-placement' },
	});
	useEffect(() => {
		if (!isDone && !isLoading && data) setResponse(data);
	}, [isDone, isLoading, data]);
	const dismiss = useCallback(
		(messageInstanceId: string) => {
			if (!response) return;
			const remaining = {
				...response,
				messages: response.messages.filter(
					(message) => message.messageInstanceId !== messageInstanceId,
				),
			};
			setResponse(remaining);
			onDismiss?.();
		},
		[response, onDismiss],
	);
	if (!isDone && error) throw error;
	const message = response?.messages[0];

	if (!message) return null;

	return (
		<Messages.render
			{...message}
			{...messageProps}
			dismiss={() => dismiss(message.messageInstanceId)}
		/>
	);
});
